"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { translateMenuLabel } from "@/lib/menuLabels";
interface HierarchyCopy {
  h2: string;
  lead: string;
  homeTitle: string;
  homeLinkText: string;
  quoteTitle: string;
  quoteRfqLink: string;
  quoteContactLink: string;
}
const HIERARCHY_COPY: Record<Lang, HierarchyCopy> = {
  en: {
    h2: "Explore site directory",
    lead: "Browse full product specifications, wire mesh types, engineering guides, and custom manufacturing solutions.",
    homeTitle: "Home",
    homeLinkText: "Overview & Capabilities",
    quoteTitle: "Request a Quote",
    quoteRfqLink: "Custom Specification RFQ",
    quoteContactLink: "Direct Engineering Inquiry"
  },
  zh: {
    h2: "浏览网站目录",
    lead: "浏览完整产品规格、丝网类型、工程指南以及定制制造解决方案。",
    homeTitle: "首页",
    homeLinkText: "概览与制造能力",
    quoteTitle: "索取报价",
    quoteRfqLink: "定制规格询价 (RFQ)",
    quoteContactLink: "直接工程咨询"
  },
  es: {
    h2: "Explorar directorio del sitio",
    lead: "Consulte especificaciones completas de productos, tipos de mallas metálicas, guías de ingeniería y soluciones de fabricación a medida.",
    homeTitle: "Inicio",
    homeLinkText: "Visión general y capacidades",
    quoteTitle: "Solicitar cotización",
    quoteRfqLink: "Cotización de especificación a medida (RFQ)",
    quoteContactLink: "Consulta directa de ingeniería"
  },
  de: {
    h2: "Seitenverzeichnis erkunden",
    lead: "Durchsuchen Sie vollständige Produktspezifikationen, Drahtgewebetypen, Ingenieurleitfäden und maßgeschneiderte Fertigungslösungen.",
    homeTitle: "Startseite",
    homeLinkText: "Überblick & Fertigungskapazitäten",
    quoteTitle: "Angebot anfordern",
    quoteRfqLink: "Anfrage für kundenspezifische Spezifikationen (RFQ)",
    quoteContactLink: "Direkte technische Beratung"
  },
  fr: {
    h2: "Explorer le plan du site",
    lead: "Consultez les spécifications complètes des produits, types de toiles métalliques, guides d'ingénierie et solutions de fabrication sur mesure.",
    homeTitle: "Accueil",
    homeLinkText: "Vue d'ensemble et capacités",
    quoteTitle: "Demander un devis",
    quoteRfqLink: "Demande de devis sur mesure (RFQ)",
    quoteContactLink: "Consultation technique directe"
  },
  pt: {
    h2: "Explorar diretório do site",
    lead: "Navegue pelas especificações completas de produtos, tipos de telas metálicas, guias de engenharia e soluções de fabricação personalizadas.",
    homeTitle: "Início",
    homeLinkText: "Visão geral e capacidades",
    quoteTitle: "Solicitar cotação",
    quoteRfqLink: "Cotação de especificação sob medida (RFQ)",
    quoteContactLink: "Consulta direta de engenharia"
  }
};
function getDisplayLabel(label: string, lang: Lang): string {
  if (!label) return "";
  return translateMenuLabel(label, lang);
}
function RecursiveMenuList({
  items,
  level = 0,
  lang
}: {
  items: MenuItem[];
  level?: number;
  lang: Lang;
}) {
  if (!items || items.length === 0) return null;
  return <ul className={level === 0 ? "space-y-2.5" : "mt-2 space-y-2 border-l border-border pl-3.5"}>
      {items.map((child, idx) => {
      const hasChildren = Boolean(child.children && child.children.length > 0);
      const labelText = getDisplayLabel(child.label, lang);
      return <li key={child.id || idx} data-index={idx} className="text-sm">
            <Link to={child.href || "#"} className="text-muted-foreground hover:text-foreground hover:underline decoration-primary/50 underline-offset-4 transition-colors font-medium inline-block py-0.5">
              {labelText}
            </Link>
            {hasChildren && <RecursiveMenuList items={child.children as MenuItem[]} level={level + 1} lang={lang} />}
          </li>;
    })}
    </ul>;
}
function MenuColumnsConsumer({
  lang
}: {
  lang: Lang;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-8 lg:gap-12">
        {Array.from({
        length: 4
      }).map((_, i) => <div key={i} data-index={i} className="space-y-4">
            <div className="h-5 w-32 bg-muted rounded-sm" />
            <div className="space-y-2.5">
              <div className="h-4 w-24 bg-muted/70 rounded-sm" />
              <div className="h-4 w-28 bg-muted/70 rounded-sm" />
              <div className="h-4 w-20 bg-muted/70 rounded-sm" />
            </div>
          </div>)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return null;
  }
  return <div className="contents">
      {menuItems.map((item, index) => {
      const hasChildren = Boolean(item.children && item.children.length > 0);
      const labelText = getDisplayLabel(item.label, lang);
      return <div key={item.id || index} data-index={index} className="flex flex-col space-y-4">
            <div>
              <Link to={item.href || "#"} className="text-base font-semibold tracking-tight text-foreground hover:text-primary transition-colors inline-block">
                {labelText}
              </Link>
            </div>
            {hasChildren && <RecursiveMenuList items={item.children as MenuItem[]} level={0} lang={lang} />}
          </div>;
    })}
    </div>;
}
export default function Hierarchy() {
  const {
    lang
  } = useLanguage();
  const c = pick(lang, HIERARCHY_COPY);
  return <section id="sitenavigationhierarchy" data-nav="light" className="bg-background text-foreground mf-section border-t border-border">
      <div className="mf-container">
        <div className="mb-10 lg:mb-14">
          <h2 className="mf-h2 text-foreground">
            {c.h2}
          </h2>
          <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-2xl">
            {c.lead}
          </p>
          <Separator className="mt-6" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-8 lg:gap-12">
          {/* Static Column 1: Home */}
          <div className="flex flex-col space-y-4">
            <div>
              <Link to="/" className="text-base font-semibold tracking-tight text-foreground hover:text-primary transition-colors inline-block">
                {c.homeTitle}
              </Link>
            </div>
            <ul className="space-y-2.5">
              <li data-index={0} className="text-sm">
                <Link to="/" className="text-muted-foreground hover:text-foreground hover:underline decoration-primary/50 underline-offset-4 transition-colors font-medium inline-block py-0.5">
                  {c.homeLinkText}
                </Link>
              </li>
            </ul>
          </div>

          {/* Dynamic Menu Columns from WordPress Header Menu */}
          <MenuProvider menu_id="4" className="contents">
            <MenuColumnsConsumer lang={lang} />
          </MenuProvider>

          {/* Static Column 2: Request a Quote */}
          <div className="flex flex-col space-y-4">
            <div>
              <Link to="/RequestAQuotePage" className="text-base font-semibold tracking-tight text-foreground hover:text-primary transition-colors inline-block">
                {c.quoteTitle}
              </Link>
            </div>
            <ul className="space-y-2.5">
              <li data-index={0} className="text-sm">
                <Link to="/RequestAQuotePage" className="text-muted-foreground hover:text-foreground hover:underline decoration-primary/50 underline-offset-4 transition-colors font-medium inline-block py-0.5">
                  {c.quoteRfqLink}
                </Link>
              </li>
              <li data-index={1} className="text-sm">
                <Link to="/ContactPage" className="text-muted-foreground hover:text-foreground hover:underline decoration-primary/50 underline-offset-4 transition-colors font-medium inline-block py-0.5">
                  {c.quoteContactLink}
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>;
}